import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

import { Input, Image, Tab, Checkbox, Divider } from "semantic-ui-react";
import { Div, Button } from "@components/Generics.react";
import ImageUploader from "@components/ImageUploader.react";

import PreviewLayout from "@layouts/PreviewLayout.react";
import { WrapperList } from "@schemas/wrapperSchema";

export default function CourseSetup(props) {
    const { course, updateCourse } = props;

    const [search, setSearch] = useState("");
    const wrapperList = _.filter(_.keys(WrapperList), (template) =>
        template.includes(search)
    );

    const [courseName, setCourseName] = useState(course.name);
    const [courseDescription, setCourseDescription] = useState(
        course.description
    );
    const [courseImage, setCourseImage] = useState(course.image);
    const [imgEditorOpen, setImgEditorOpen] = useState(false);
    const { t } = useTranslation("common");

    function saveCourseImage(image) {
        setCourseImage(image);
        setImgEditorOpen(false);
    }

    async function saveCourse() {
        await updateCourse({
            id: course._id,
            name: courseName,
            image: courseImage,
            description: courseDescription,
        }).unwrap();
        toast(t("components.saved"));
    }

    async function changeWrapper(selectedWrapper) {
        await updateCourse({
            id: course._id,
            wrapper: selectedWrapper,
        }).unwrap();
        toast(t("builder.course.themeUpdated"));
    }

    async function toggleTiming() {
        await updateCourse({
            id: course._id,
            enable_timing: !course.enable_timing,
        }).unwrap();
        toast(
            course.enable_timing
                ? `${t("builder.course.timed")} ${t("components.disabled")}!`
                : `${t("builder.course.timed")} ${t("components.enabled")}!`
        );
    }

    async function toggleSequential() {
        await updateCourse({
            id: course._id,
            sequential: !course.sequential,
        }).unwrap();
        toast(
            course.sequential
                ? `${t("builder.course.sequential")} ${t(
                      "components.disabled"
                  )}!`
                : `${t("builder.course.sequential")} ${t(
                      "components.enabled"
                  )}!`
        );
    }

    async function toggleAutoNotification() {
        await updateCourse({
            id: course._id,
            autoNotification: !course.autoNotification,
        }).unwrap();
        toast(
            course.autoNotification
                ? `${t("builder.course.autoNot")} ${t("components.disabled")}!`
                : `${t("builder.course.autoNot")} ${t("components.enabled")}!`
        );
    }

    async function toggleReminders() {
        await updateCourse({
            id: course._id,
            notifications: {
                ...course.notifications,
                ...{ reminders: !course.notifications.reminders },
            },
        }).unwrap();
        toast(
            course.notifications.reminders
                ? `${t("builder.course.reminders")} ${t(
                      "components.disabled"
                  )}!`
                : `${t("builder.course.reminders")} ${t("components.enabled")}!`
        );
    }

    async function updateCourseDate(thisDate, dateType) {
        await updateCourse({
            id: course._id,
            dates: { ...course.dates, ...{ [dateType]: thisDate } },
        }).unwrap();
        toast(t("builder.course.accessDtUpdated"));
    }

    async function setPriority(value) {
        await updateCourse({
            id: course._id,
            priority: value,
        }).unwrap();
        toast(t("builder.course.priorityUpdated"));
    }

    async function updateVintage(days) {
        await updateCourse({
            id: course._id,
            vintage: days,
        }).unwrap();
        toast(t("builder.course.vintageUpdated"));
    }

    async function updateReminderProps(value, target) {
        await updateCourse({
            id: course._id,
            notifications: { ...course.notifications, ...{ [target]: value } },
        }).unwrap();
        toast(t("builder.course.remindersUpdated"));
    }

    const panes = [
        {
            menuItem: t("builder.course.theme"),
            render: () => (
                <Tab.Pane>
                    <Div ht="400px" autoOverflowY>
                        <Div bold medpad flex spaceBetween>
                            <Div big>{t("builder.course.selectTheme")}:</Div>
                            <Input
                                icon="search"
                                placeholder={t("components.search")}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Div>
                        <Div clear flex flexStart gapped>
                            {_.map(wrapperList, (wrapper, idx) => {
                                return (
                                    <Div
                                        wd="calc(33.33% - 7.5px)"
                                        key={`wrapper-${idx}`}
                                        float-left
                                        medpad
                                        rounded
                                        clickable
                                        vapor={wrapper !== course.wrapper}
                                        charcoal={wrapper === course.wrapper}
                                        onClick={() => changeWrapper(wrapper)}
                                    >
                                        <Image
                                            src={`/assets/images/wrappers/${wrapper}/play_backdrop.png`}
                                        />
                                        <Div uppercase center-align gapTop>
                                            {wrapper.replace(/_/g, " ")}
                                        </Div>
                                    </Div>
                                );
                            })}
                        </Div>
                    </Div>
                </Tab.Pane>
            ),
        },
        {
            menuItem: t("builder.course.access"),
            render: () => (
                <Tab.Pane>
                    <Div ht="400px" autoOverflowY>
                        <Div basepad ivory snug>
                            <Div bold big>
                                {t("builder.course.setSequential")}:
                            </Div>
                            <Div italics gapBottom>
                                {t("builder.course.sequentialHelp")}
                            </Div>
                            <Checkbox
                                toggle
                                label={t("builder.course.isSequential")}
                                checked={course.sequential}
                                onChange={toggleSequential}
                            />
                        </Div>
                        <Div basepad ivory snug>
                            <Div big bold>
                                {t("builder.course.setPriority")}
                            </Div>
                            <Div italics gapBottom>
                                {t("builder.course.priorityHelp")}
                            </Div>
                            <Input
                                type="number"
                                value={course.priority}
                                onChange={(e) => setPriority(e.target.value)}
                            />
                        </Div>
                    </Div>
                </Tab.Pane>
            ),
        },
        {
            menuItem: t("builder.course.timing"),
            render: () => (
                <Tab.Pane>
                    <Div bold medpad flex spaceBetween>
                        <Div big>{t("builder.course.setTiming")}:</Div>
                    </Div>
                    <Div medpad>
                        <Div gutter>
                            <Checkbox
                                toggle
                                label={t("builder.course.checkTiming")}
                                checked={course.enable_timing}
                                onChange={toggleTiming}
                            />
                        </Div>
                        {course.enable_timing && (
                            <Div basepad ivory>
                                <Div flex spaceBetween gapped gutter>
                                    <Div wd="49%">
                                        <Div bold gapBottom>
                                            {t("builder.course.startDt")}
                                        </Div>
                                        <DatePicker
                                            dateFormat="dd MMMM yyyy h:mm a"
                                            selected={
                                                course.dates?.start_date &&
                                                moment(
                                                    course.dates.start_date
                                                ).toDate()
                                            }
                                            onChange={(date) =>
                                                updateCourseDate(
                                                    date,
                                                    "start_date"
                                                )
                                            }
                                            showTimeSelect={true}
                                        />
                                    </Div>
                                    <Div wd="49%">
                                        <Div bold gapBottom>
                                            {t("builder.course.endDt")}
                                        </Div>
                                        <DatePicker
                                            dateFormat="dd MMMM yyyy h:mm a"
                                            selected={
                                                course.dates?.end_date &&
                                                moment(
                                                    course.dates.end_date
                                                ).toDate()
                                            }
                                            onChange={(date) =>
                                                updateCourseDate(
                                                    date,
                                                    "end_date"
                                                )
                                            }
                                            showTimeSelect={true}
                                        />
                                    </Div>
                                </Div>
                                <Divider horizontal>
                                    {t("components.or")}
                                </Divider>
                                <Div center-align>
                                    <Input
                                        label={t("builder.course.vintage")}
                                        type="number"
                                        value={course.vintage}
                                        onChange={(e) =>
                                            updateVintage(e.target.value)
                                        }
                                    />{" "}
                                    {t("components.days")}
                                    <Div gapTop italics gutter>
                                        {t("builder.course.vintageHelp")}
                                    </Div>
                                </Div>
                            </Div>
                        )}
                    </Div>
                </Tab.Pane>
            ),
        },
        {
            menuItem: t("builder.course.notifications"),
            render: () => (
                <Tab.Pane>
                    <Div bold medpad flex spaceBetween>
                        <Div big>{t("builder.course.notiSettings")}:</Div>
                    </Div>
                    <Div medpad>
                        <Div gapBottom>
                            <Checkbox
                                toggle
                                label={t("builder.course.notiSend")}
                                checked={course.autoNotification}
                                onChange={toggleAutoNotification}
                            />
                        </Div>
                        <Div gutter>
                            <Checkbox
                                toggle
                                label={t("builder.course.notiReminders")}
                                checked={course.notifications?.reminders}
                                onChange={toggleReminders}
                            />
                        </Div>
                        {course.notifications?.reminders && (
                            <Div medpad ivory>
                                <Div>
                                    <Input
                                        label={t("builder.course.notiAfter")}
                                        type="number"
                                        value={course.notifications?.inactivity}
                                        onChange={(e) =>
                                            updateReminderProps(
                                                e.target.value,
                                                "inactivity"
                                            )
                                        }
                                    />{" "}
                                    {t("builder.course.notiInactivity")}
                                </Div>
                                <Div>
                                    <Input
                                        label={t("builder.course.notiMax")}
                                        type="number"
                                        value={course.notifications?.max}
                                        onChange={(e) =>
                                            updateReminderProps(
                                                e.target.value,
                                                "max"
                                            )
                                        }
                                    />{" "}
                                    {t("builder.course.notiMaxNo")}
                                </Div>
                            </Div>
                        )}
                    </Div>
                </Tab.Pane>
            ),
        },
    ];

    // ========================= Render Function =================================
    return (
        <Div fluid fullht padTop flex noWrap spaceAround>
            <Div fullht wd="calc(100% - 320px)" basepad>
                <Div large uppercase gutter bold>
                    {t("components.editing")}: {course?.name}
                </Div>
                <Div ivory fluid gutter snubbed noOverflow flex>
                    <Div grey relative wd="280px" minHt="140px">
                        <Image src={courseImage} />
                        <Div
                            absolute
                            layer={3}
                            style={{ bottom: "10px", right: "10px" }}
                        >
                            <Div flex clickable gapped>
                                <Button
                                    size="mini"
                                    content={t("builder.dic.changeImage")}
                                    secondary
                                    onClick={() => setImgEditorOpen(true)}
                                />
                            </Div>
                            <ImageUploader
                                modalOpen={imgEditorOpen}
                                handleModalClose={() => setImgEditorOpen(false)}
                                handleModalSave={saveCourseImage}
                                value={courseImage}
                                aspectRatio={0.5}
                                mWidth={640}
                            />
                        </Div>
                    </Div>
                    <Div medpad wd="calc(100% - 280px)">
                        <Input
                            fluid
                            label={t("builder.dic.name")}
                            value={courseName}
                            onChange={(e, { value }) => setCourseName(value)}
                        />
                        <Input
                            fluid
                            label={t("builder.dic.description")}
                            value={courseDescription}
                            onChange={(e, { value }) =>
                                setCourseDescription(value)
                            }
                        />
                        <Div fluid gapTop small>
                            <Button
                                primary
                                content={t("components.update")}
                                onClick={saveCourse}
                            />
                        </Div>
                    </Div>
                </Div>
                <Div smoke fluid>
                    <Tab panes={panes} />
                </Div>
            </Div>
            <PreviewLayout
                key={`course-preview-${course.wrapper}`}
                url={`/courses/${course._id}`}
                message={
                    course.wrapper &&
                    course.wrapper !== "none" &&
                    t("builder.dic.previewHelp")
                }
            />
        </Div>
    );
}
